import { Box, Button, Col, Row, Text } from '@opendoor/bricks-next';
import { IconCircleCheckFilled } from '@tabler/icons-react';
import { EntryComponent } from 'cms/entries/entries';
import { IFcProductOptions } from 'declarations/contentful';

import { FCContentContainer, FCContentContainerWrapper } from './shared/FCLayout';

const ProductCard = ({
  tag,
  title,
  subtitle,
  bullets,
}: {
  tag: string;
  title: string;
  subtitle: string;
  bullets: string[];
}) => {
  return (
    <Col
      borderRadius="$6x"
      overflow="hidden"
      style={{ backgroundColor: '#ece9e7' }}
      flex={1}
      $largerThanSM={{ flexBasis: 0 }}
    >
      <Row
        style={{ backgroundColor: '#051a44' }}
        px="$12x"
        $largerThanMD={{ px: '$16x' }}
        py="$10x"
      >
        <Text
          tag="span"
          typography="$labelSmall"
          textTransform="uppercase"
          style={{ color: '#FFF' }}
        >
          {tag}
        </Text>
      </Row>
      <Col padding="$12x" $largerThanMD={{ padding: '$24x' }} gap="$12x">
        <Col gap="$4x">
          <Text
            tag="h3"
            typography="$subheaderMedium"
            $largerThanMD={{ typography: '$headerMedium' }}
            color="$contentAccentBrand"
          >
            {title}
          </Text>
          <Text
            tag="p"
            typography="$labelSmall"
            $largerThanMD={{ typography: '$labelXlarge' }}
            color="$contentPrimary"
          >
            {subtitle}
          </Text>
        </Col>
        <Col gap="$6x">
          {bullets.map((bullet, i) => (
            <Row
              key={i}
              alignItems="center"
              gap="$4x"
              $largerThanMD={{ gap: '$12x' }}
              justifyContent="flex-start"
            >
              <Box flexShrink={0} width={24} height={24} $largerThanMD={{ width: 32, height: 32 }}>
                <IconCircleCheckFilled
                  size={32}
                  color="#0040e6"
                  style={{ width: '100%', height: '100%' }}
                />
              </Box>
              <Text
                tag="span"
                typography="$bodyMedium"
                color="$contentPrimary"
                $largerThanMD={{ typography: '$bodyXlarge' }}
              >
                {bullet}
              </Text>
            </Row>
          ))}
        </Col>
      </Col>
    </Col>
  );
};

const RenderFCProductOptions = (entry: IFcProductOptions) => {
  const { fields } = entry;

  return (
    <FCContentContainerWrapper>
      <FCContentContainer>
        <Col
          alignItems="center"
          justifyContent="center"
          py="$40x"
          gap="$12x"
          $largerThanSM={{ gap: '$24x' }}
        >
          <Text
            tag="h2"
            $largerThanMD={{ typography: '$displayXsmall' }}
            typography="$headerMedium"
            textAlign="center"
            color="$contentPrimary"
            maxWidth={700}
          >
            {fields.title}
          </Text>
          <Box
            flexDirection="column"
            width="100%"
            gap="$5x"
            $largerThanSM={{ flexDirection: 'row' }}
          >
            <ProductCard
              tag={fields.card1Tag}
              title={fields.card1Title}
              subtitle={fields.card1Subtitle}
              bullets={[fields.card1Bullet1, fields.card1Bullet2, fields.card1Bullet3]}
            />
            <ProductCard
              tag={fields.card2Tag}
              title={fields.card2Title}
              subtitle={fields.card2Subtitle}
              bullets={[fields.card2Bullet1, fields.card2Bullet2, fields.card2Bullet3]}
            />
          </Box>
          <Col gap="$6x" $largerThanSM={{ gap: '$12x' }} alignItems="center">
            <Text
              tag="p"
              typography="$labelMedium"
              $largerThanSM={{ typography: '$subheaderXsmall' }}
              textAlign="center"
            >
              {fields.footerText}
            </Text>
            <Button
              variant="primary"
              size="medium"
              label={fields.ctaButtonText}
              analyticsName="cosmos-fc-product-options-cta"
              href={fields.ctaUrl}
            />
          </Col>
        </Col>
      </FCContentContainer>
    </FCContentContainerWrapper>
  );
};

const FCProductOptions: EntryComponent<IFcProductOptions> = {
  render: RenderFCProductOptions,
};

export default FCProductOptions;
